/* eslint-disable import/order */
import * as yup from 'yup'
import { ValidationError } from 'yup'
import { SettingAdapterConfig } from './settings'

// TODO: eliminate V1 imports
// v1 import
import { EMAIL_REGEX } from 'lib/validation'

const any = yup.mixed().nullable()

const email = yup
  .string()
  .typeError('field_errors.invalid_value')
  .test({
    name: 'is-email',
    test: (value, ctx) => {
      if (typeof value === 'undefined' || value.trim() === '') {
        return true
      }
      if (!EMAIL_REGEX.test(value)) {
        return ctx.createError({ message: 'field_errors.invalid_email' })
      }
      return true
    }
  })

const dynamicEnum = (allowedValues: string[]) =>
  yup.string().typeError('field_errors.invalid_value').oneOf(allowedValues).required('field_errors.required_value')

const requiredEmailOnAllowedDomain = (allowedDomains: string[]) =>
  yup
    .string()
    .typeError('field_errors.invalid_value')
    .test({
      name: 'is-email-on-allowed-domain',
      test: (value, ctx) => {
        try {
          if (typeof value === 'undefined' || value.trim() === '') {
            return ctx.createError({ message: 'field_errors.required_value' })
          }
          email.validateSync(value)
          const host = value.trim().split('@')[1]
          const allowedDomainSchema = dynamicEnum(allowedDomains.map(d => d.toLowerCase()))
          try {
            allowedDomainSchema.validateSync(host.toLowerCase())
          } catch (e) {
            return ctx.createError({ message: 'field_errors.email_domain_not_allowed' })
          }
          return true
        } catch (e) {
          return ctx.createError({ message: 'field_errors.invalid_value' })
        }
      }
    })

const optionalEmailOnAllowedDomain = (allowedDomains: string[]) =>
  yup
    .string()
    .typeError('field_errors.invalid_value')
    .test({
      name: 'is-email-on-allowed-domain',
      test: (value, ctx) => {
        try {
          if (typeof value === 'undefined' || value.trim() === '') {
            return true
          }
          email.validateSync(value)
          const host = value.trim().split('@')[1]
          const allowedDomainSchema = dynamicEnum(allowedDomains.map(d => d.toLowerCase()))
          try {
            allowedDomainSchema.validateSync(host.toLowerCase())
          } catch (e) {
            return ctx.createError({ message: 'field_errors.email_domain_not_allowed' })
          }
          return true
        } catch (e) {
          return ctx.createError({ message: 'field_errors.invalid_value' })
        }
      }
    })

export const schemas = {
  any,
  setting_0_1: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['0', '1'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  setting_0_1_2: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['0', '1', '2'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  inbound_schedule: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['never', 'custom'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  custom_schedule: any,
  outbound_schedule: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['never', 'custom', 'immediately'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  timezone: yup.string().required('field_errors.required_value'),
  scan_policy: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['scan', 'allow', 'block'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  required_email: email.required('field_errors.required_value'),
  optional_email: email,
  optional_csv_email_list: yup
    .string()
    .typeError('field_errors.invalid_value')
    .test({
      name: 'is-csv-email-list',
      test: (value, ctx) => {
        if (typeof value === 'undefined' || value.trim() === '') {
          return true
        }
        const arrayValue = value.split(',').map(item => item.trim())
        const localSchema = yup.array().of(email)
        try {
          localSchema.validateSync(arrayValue)
          return true
        } catch (e) {
          return ctx.createError({ message: 'field_errors.invalid_value' })
        }
      }
    }),
  required_csv_email_list: yup
    .string()
    .typeError('field_errors.invalid_value')
    .test({
      name: 'is-csv-email-list',
      test: (value, ctx) => {
        if (typeof value === 'undefined' || value.trim() === '') {
          return ctx.createError({ message: 'field_errors.required_value' })
        }
        const arrayValue = value.split(',').map(item => item.trim())
        const localSchema = yup.array().of(email)
        try {
          localSchema.validateSync(arrayValue)
          return true
        } catch (e) {
          return ctx.createError({ message: 'field_errors.invalid_value' })
        }
      }
    }),
  radio_on_off: yup
    .string()
    .typeError('field_errors.invalid_value')
    .oneOf(['on', 'off'], 'field_errors.invalid_value')
    .required('field_errors.required_value'),
  required_string: yup.string().typeError('field_errors.invalid_value').required('field_errors.required_value'),
  optional_string: yup.string().typeError('field_errors.invalid_value'),
  required_email_on_allowed_domain: requiredEmailOnAllowedDomain,
  optional_email_on_allowed_domain: optionalEmailOnAllowedDomain,
  required_email_on_allowed_domain_csv: (allowedDomains: string[]) =>
    yup
      .string()
      .typeError('field_errors.invalid_value')
      .test({
        name: 'is-required_email_on_allowed_domain_csv',
        test: (value, ctx) => {
          if (typeof value === 'undefined' || value.trim() === '') {
            return ctx.createError({ message: 'field_errors.required_value' })
          }
          const arrayValue = value.split(',').map(item => item.trim())
          const localSchema = yup.array().of(requiredEmailOnAllowedDomain(allowedDomains))
          try {
            localSchema.validateSync(arrayValue)
            return true
          } catch (e) {
            if (e instanceof ValidationError) {
              return e
            }
            return ctx.createError({ message: 'field_errors.invalid_value' })
          }
        }
      })
}

export const SchemaFactory = {
  fromSettings: (settings: SettingAdapterConfig[]) => {
    const shape: Record<string, any> = {}
    settings.forEach(setting => {
      shape[setting.inputName] = setting.schema
    })
    return yup.object().shape(shape)
  }
}

export const validateInContext =
  <TType, TContext, TDefault, TFlags extends yup.Flags>(
    schema: yup.Schema<TType, TContext, TDefault, TFlags>,
    ctx: yup.TestContext<yup.AnyObject>
  ) =>
  (value: TType): { result: true } | { result: false; error: ValidationError } => {
    try {
      schema.validateSync(value)
      return { result: true }
    } catch (e) {
      if (e instanceof ValidationError) {
        return { result: false, error: ctx.createError({ message: e.message || 'field_errors.invalid_value' }) }
      }
      return { result: false, error: ctx.createError({ message: 'field_errors.invalid_value' }) }
    }
  }
